<template>
  <v-dialog
    v-model="$parent.fundPerformanceDialog"
    :fullscreen="$vuetify.display.xs"
    persistent
    scrollable
    width="650px"
  >
    <v-card>
      <!-- Header -->
      <v-row no-gutters class="bg-grey-lighten-4 pl-6 py-2 primary" align="center">
        <v-col>
          <h4 class="text-h6">Dynamic Fund Selection Each Quarter</h4>
        </v-col>
      </v-row>

      <!-- Body -->
      <v-card-text style="height: 450px">
        <v-container fluid pa-0>
          <v-row>
            <!-- Note -->
            <v-col cols="12" class="text-body-1">
              <v-alert :value="true" type="info" class="d-inline-flex">
                Funds are selected dynamically each quarter based on the attributes you specify
                below:
              </v-alert>
            </v-col>

            <!-- Matches found -->
            <v-col cols="12">
              <h4 class="text-subtitle-1">
                Matching funds from latest quarter:
                <v-chip disabled class="text-body-2 text-black">
                  <v-progress-circular
                    v-if="searching"
                    :size="16"
                    :width="2"
                    indeterminate
                    color="primary"
                  />
                  <span v-else>{{ filerPerformanceMatches }}</span>
                </v-chip>
              </h4>
            </v-col>

            <!-- How do you want funds chosen? -->
            <v-col cols="12">
              <helpers-section-sub-header>How do you want funds sorted?</helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.top_performing_type"
                :items="topPerformingTypes"
                :disabled="!accessAllowed({ requires_pro_subscription: false })"
                item-title="description"
                return-object
                variant="outlined"
                density="compact"
                style="max-width: 400px"
              />
            </v-col>

            <!-- Maximum Filers to use each quarter -->
            <v-col cols="12" sm="6" md="4">
              <helpers-section-sub-header>Max Funds Per quarter</helpers-section-sub-header>
              <v-text-field
                v-model="filerPerformance.max_filers"
                :disabled="!accessAllowed({ requires_pro_subscription: false }) || !isEnabled"
                :rules="[value => value >= 1 || 'Maximum funds must set to at least 1']"
                type="number"
                variant="outlined"
                density="compact"
                persistent-hint
                style="max-width: 400px"
              />
            </v-col>

            <!-- Quarterly turnover % -->
            <v-col cols="12" sm="6" md="4">
              <helpers-section-sub-header>Quarterly turnover %</helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.turnover_type"
                :items="turnoverChoices"
                :disabled="!accessAllowed({ requires_pro_subscription: false }) || !isEnabled"
                item-title="description"
                return-object
                variant="outlined"
                density="compact"
                style="max-width: 400px"
              />
            </v-col>

            <!-- % investing styles -->
            <v-col cols="12" sm="12" md="6">
              <helpers-section-sub-header
                >Investing Styles to Include (All Must Match)</helpers-section-sub-header
              >
              <v-select
                v-model="filerPerformance.investing_styles"
                :items="investing_styles"
                :menu-props="{ maxHeight: '400' }"
                multiple
                variant="outlined"
                density="compact"
                hint="Leave blank for all"
                persistent-hint
              ></v-select>
            </v-col>

            <!-- % investing styles -->
            <v-col cols="12" sm="12" md="6">
              <helpers-section-sub-header
                >Investing Styles to Include (Any That Match)</helpers-section-sub-header
              >
              <v-select
                v-model="filerPerformance.investing_styles_any"
                :items="investing_styles"
                :menu-props="{ maxHeight: '400' }"
                multiple
                variant="outlined"
                density="compact"
                hint="Leave blank for all"
                persistent-hint
              ></v-select>
            </v-col>

            <!-- % investing styles -->
            <v-col cols="12" sm="12" md="6">
              <helpers-section-sub-header
                >Investing Styles to Exclude (Any Can Match)
              </helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.exclude_investing_styles"
                :items="investing_styles"
                :menu-props="{ maxHeight: '400' }"
                multiple
                variant="outlined"
                density="compact"
                hint="Leave blank for all"
                persistent-hint
              ></v-select>
            </v-col>

            <!-- Fund Classification filter -->
            <v-col cols="12" sm="12" md="6">
              <helpers-section-sub-header>Fund Classification</helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.fund_classifications"
                :items="fund_classifications"
                :menu-props="{ maxHeight: '400' }"
                multiple
                item-title="description"
                return-object
                variant="outlined"
                density="compact"
                hint="Leave blank for all"
                persistent-hint
              ></v-select>
            </v-col>

            <!-- % top 10 concentration -->
            <v-col cols="12" sm="6" md="4">
              <helpers-section-sub-header>% top 10 concentration</helpers-section-sub-header>
              <v-select
                v-model="filerPerformance.top10_type"
                :items="top10Choices"
                :disabled="!accessAllowed({ requires_pro_subscription: false }) || !isEnabled"
                item-title="description"
                return-object
                variant="outlined"
                density="compact"
                style="max-width: 400px"
              />
            </v-col>

            <!-- # of Holdings in 13F Portfolio -->
            <v-col cols="12" sm="6">
              <helpers-section-sub-header
                ># of Holdings in 13F Portfolio</helpers-section-sub-header
              >
              <v-switch
                v-model="filerPerformance.use_holdings"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Number of Holdings"
                hide-details
                density="compact"
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.holdings_from_to[0]"
                    :disabled="!filerPerformance.use_holdings"
                    label="From"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.holdings_from_to[1]"
                    :disabled="!filerPerformance.use_holdings"
                    label="To"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- Insider Ownership of Private Funds -->
            <v-col cols="12" sm="6" md="6">
              <helpers-section-sub-header
                >Insider Ownership of Private Funds</helpers-section-sub-header
              >
              <p>
                Enabling this option sets the earliest allowable start date to 2011. This is the
                earliest date this information was made available.
              </p>
              <v-text-field
                v-model="filerPerformance.adv_insider_ownership"
                :disabled="!accessAllowed({ requires_pro_subscription: false }) || !isEnabled"
                mask="###"
                suffix="%"
                label="Minimum"
                variant="outlined"
                density="compact"
                style="max-width: 400px"
              />
            </v-col>

            <!-- Market Cap of Fund's 13F Portfolio -->
            <v-col cols="12">
              <helpers-section-sub-header>AUM of Fund's 13F Portfolio</helpers-section-sub-header>
              <v-switch
                v-model="filerPerformance.use_mc"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Market Cap"
                hide-details
                density="compact"
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.mc_from_to[0]"
                    :disabled="!filerPerformance.use_mc"
                    label="From (rounded to $1million)"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.mc_from_to[1]"
                    :disabled="!filerPerformance.use_mc"
                    label="To (rounded to $1million)"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12">
              <helpers-section-sub-header>Performance</helpers-section-sub-header>
              <v-switch
                v-model="filerPerformance.outperformed_s_and_p"
                color="primary"
                :disabled="!isEnabled"
                label="Only use manager's that have outperformed the S&P for the past 5 years"
                hide-details
                density="compact"
              />
              <v-switch
                v-model="filerPerformance.positive_historical_performance"
                color="primary"
                :disabled="!isEnabled"
                label="Only use manager's that had positive cumulative performance over time"
                hide-details
                density="compact"
              />
            </v-col>

            <!-- mgr weight outperformed -->
            <v-col cols="12">
              <helpers-section-sub-header>Limit managers to:</helpers-section-sub-header>
              <v-switch
                v-model="filerPerformance.mgr_weight_outperformed_equal"
                color="primary"
                :disabled="!isEnabled"
                label="Only use manager's whose 3-Year manager-weighted performance was better than their equal-weighted performance"
                hide-details
                density="compact"
                @click="disableEqualOutperform(filerPerformance.mgr_weight_outperformed_equal)"
              />
              <v-switch
                v-model="filerPerformance.equal_weight_outperformed_manager"
                color="primary"
                :disabled="!isEnabled"
                label="Only use manager's whose 3-Year equal-weighted performance was better than their manager-weighted performance"
                hide-details
                density="compact"
                @click="disableMgrOutperform(filerPerformance.equal_weight_outperformed_manager)"
              />
            </v-col>

            <!-- Avg. Time Held Top 10 (quarterly) -->
            <v-col cols="12" sm="6">
              <helpers-section-sub-header
                >Avg. Time Held Top 10 (quarterly)</helpers-section-sub-header
              >
              <v-switch
                v-model="filerPerformance.use_time_held_top10"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Avg. Time Held Top 10"
                hide-details
                density="compact"
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.time_held_top10_from_to[0]"
                    :disabled="!filerPerformance.use_time_held_top10"
                    label="From"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.time_held_top10_from_to[1]"
                    :disabled="!filerPerformance.use_time_held_top10"
                    label="To"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- # of Holdings in 13F Portfolio -->
            <v-col cols="12" sm="6">
              <helpers-section-sub-header
                >Min/Max number of quarterly 13F Filings</helpers-section-sub-header
              >
              <p>
                Use to limit to funds with a minimum amount of 13F history or use to restrict to new
                funds only.
              </p>
              <v-switch
                v-model="filerPerformance.use_quarters"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Number of Quarterly 13F Filings"
                hide-details
                density="compact"
              />
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.quarters_min"
                    :disabled="!filerPerformance.use_quarters"
                    label="Min"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.quarters_max"
                    :disabled="!filerPerformance.use_quarters"
                    label="Max"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="12">
              <helpers-section-sub-header>Winning Buys History</helpers-section-sub-header>
              <p>
                Looks at Winning Buys (new and adds) from a manager's top 20 holdings. To be
                considered a winner, the stock must have outperformed the S&P 500 during the
                timeframe.
              </p>
              <v-switch
                v-model="filerPerformance.use_winning_buys"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Winning Buy History"
                hide-details
                density="compact"
              />
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="filerPerformance.winning_buys_min"
                    :disabled="!filerPerformance.use_winning_buys"
                    label="Min. Overall Winning Percentage Past 5 Years"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="filerPerformance.winning_buys_1_qtr_min"
                    :disabled="!filerPerformance.use_winning_buys"
                    label="Min. Average Winning Percentage after 1-Qtr Past 5 Years"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="filerPerformance.winning_buys_4_qtr_min"
                    :disabled="!filerPerformance.use_winning_buys"
                    label="Min. Average Winning Percentage after 4-Qtrs Past 5 Years"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="filerPerformance.winning_buys_total"
                    :disabled="!filerPerformance.use_winning_buys"
                    label="Min. Total # of Winning Buys Past 8 Quarters"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="12">
              <helpers-section-sub-header>Impact Buys</helpers-section-sub-header>
              <p>
                Looks at Impact Winning Buys that represented at least 2% of the overall portfolio.
                To be considered a winner, the stock must have outperformed the S&P 500 during the
                timeframe.
              </p>
              <v-switch
                v-model="filerPerformance.use_impact_buys"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Impact Buy History"
                hide-details
                density="compact"
              />
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="filerPerformance.impact_buys_min"
                    :disabled="!filerPerformance.use_impact_buys"
                    label="Min. Number of Winning Impact Buys Past 3 Years"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="filerPerformance.impact_percent_min"
                    :disabled="!filerPerformance.use_impact_buys"
                    label="Min. Average Winning Impact Buy Percentage Past 3 Years"
                    variant="outlined"
                    density="compact"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" sm="6">
              <helpers-section-sub-header>Market Cap Concentration</helpers-section-sub-header>
              <v-switch
                v-model="filerPerformance.use_market_cap_range"
                color="primary"
                :disabled="!isEnabled"
                label="Enable filtering by Market Cap"
                hide-details
              />
              <v-row no-gutters>
                <v-col cols="12">
                  <v-range-slider
                    v-model="filerPerformance.market_cap_range"
                    label="Set Market Cap Range"
                    :disabled="!filerPerformance.use_market_cap_range"
                    :tick-labels="market_cap_labels"
                    :value="[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
                    min="1"
                    max="10"
                    vertical
                    show-ticks="always"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.market_cap_range_min"
                    :disabled="!filerPerformance.use_market_cap_range"
                    label="% of port. in MC range above "
                    persistent-hint
                    variant="outlined"
                    density="compact"
                    hint="Entering the lower range for the % of portfolio that is invested in the market cap range selected above"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="filerPerformance.market_cap_range_max"
                    :disabled="!filerPerformance.use_market_cap_range"
                    label="To"
                    persistent-hint
                    variant="outlined"
                    density="compact"
                    hint="Entering the upper range for the % of portfolio that is invested in the market cap range selected above"
                    style="max-width: 200px"
                  />
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12">
              <helpers-section-sub-header>Sector Concentration</helpers-section-sub-header>
              <v-row no-gutters>
                <v-col v-for="item in filerPerformance.sc.items" :key="item.id" cols="12">
                  <v-switch
                    v-model="item.selected"
                    :label="item.name"
                    color="primary"
                    hide-details
                    density="compact"
                    title="Click to toggle filter"
                    class="mt-0"
                  />
                  <v-slide-y-transition>
                    <v-container v-if="item.selected" pa-0 fluid>
                      <v-row align="end" class="my-2">
                        <v-col cols="4">
                          <v-text-field
                            v-model.number="item.from"
                            :error="Boolean(item.error)"
                            :prefix="filerPerformance.sc.prefix"
                            :suffix="filerPerformance.sc.suffix"
                            type="number"
                            label="FROM"
                            placeholder=" "
                            hide-details
                            variant="outlined"
                            density="compact"
                            @input="resetErrorState(item)"
                            @keyup.enter="updateDynamicNumberRange(filerPerformance.sc, item)"
                          />
                        </v-col>
                        <v-col cols="4">
                          <v-text-field
                            v-model.number="item.to"
                            :error="Boolean(item.error)"
                            :prefix="filerPerformance.sc.prefix"
                            :suffix="filerPerformance.sc.suffix"
                            type="number"
                            label="TO"
                            placeholder=" "
                            hide-details
                            variant="outlined"
                            density="compact"
                            @input="resetErrorState(item)"
                            @keyup.enter="updateDynamicNumberRange(filerPerformance.sc, item)"
                          />
                        </v-col>

                        <v-col v-if="item.error" cols="12" class="v-messages text-error">
                          {{ item.error }}
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-slide-y-transition>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <helpers-section-sub-header>Performance Metrics Filter</helpers-section-sub-header>
              <p>
                Restrict to a percentile ranking range. All funds are ranked against every other
                fund. A lower number indicates a higher ranking. For example entering a max of 20
                would only return funds whose ranking was in the top 20 of all funds
              </p>
              <v-switch
                v-model="filerPerformance.use_rankings"
                color="primary"
                :disabled="!isEnabled"
                label="Enable Performance Metrics Filter"
                hide-details
                density="compact"
              />
              <v-row density="compact">
                <v-col cols="12" md="3">
                  <v-select
                    v-model="newRanking.type"
                    label="Choose Metric"
                    :items="availableRankings"
                    :disabled="!filerPerformance.use_rankings"
                    item-title="description"
                    return-object
                    variant="outlined"
                    density="compact"
                    style="max-width: 400px"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="newRanking.min"
                    :disabled="!filerPerformance.use_rankings"
                    label="Min"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="newRanking.max"
                    :disabled="!filerPerformance.use_rankings"
                    label="Max"
                    variant="outlined"
                    density="compact"
                    style="max-width: 200px"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn :disabled="!validateRanking(newRanking)" @click="saveRanking()">
                    Add
                  </v-btn>
                </v-col>
                <v-col cols="12">
                  <v-data-table
                    :headers="ranking_headers"
                    :items="filerPerformance.rankings"
                    class="mt-1 mb-1"
                  >
                    <template #item.action="{ item }">
                      <v-icon small class="mr-2" @click="editRanking(item)">
                        {{ mdiPencil }}
                      </v-icon>

                      <v-icon small @click="deleteRanking(item)">
                        {{ mdiDelete }}
                      </v-icon>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-dialog v-model="rankingDialog" max-width="700px">
            <v-card>
              <v-card-title>
                <span class="text-h5">Edit Ranking Filter</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row density="compact">
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="editedRanking.type"
                        variant="outlined"
                        density="compact"
                        item-title="description"
                        hide-details
                        :disabled="true"
                        :items="rankings"
                        class="shrink"
                      />
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="editedRanking.min"
                        variant="outlined"
                        density="compact"
                        hide-details
                        label="min"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="editedRanking.max"
                        variant="outlined"
                        density="compact"
                        hide-details
                        label="max"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <div class="flex-grow-1" />
                <v-btn @click="cancelEditRanking"> Cancel </v-btn>
                <v-btn :disabled="!validateRanking(editedRanking)" @click="saveRanking()">
                  Add
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="$parent.fundPerformanceDialog = false"> Close </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Modules
import api from '@/store/api'
// import { mapFields } from '@/store/helpers'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'
import { mapStoreState, mapFields } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Mixins
import AccessMixin from '@/mixins/access'
import { mdiDelete, mdiPencil } from '@mdi/js'

export default {
  mixins: [AccessMixin],
  props: {
    whaleTrader: Boolean
  },
  data: () => ({
    mdiDelete,
    mdiPencil,
    newRanking: { type: null, min: null, max: null },
    rankingDialog: false,
    ranking_headers: [
      {
        title: 'Type',
        align: 'left',
        sortable: true,
        key: 'type.description'
      },
      {
        title: 'min',
        align: 'left',
        sortable: true,
        key: 'min'
      },
      {
        title: 'max',
        align: 'left',
        sortable: true,
        key: 'max'
      },
      { title: 'Actions', key: 'action', sortable: false }
    ],
    editedIndex: -1,
    editedRanking: { type: null, min: null, max: null },
    searching: false,
    filerPerformanceMatches: 0,
    market_cap_labels: [
      'Over $200B',
      '$100B to $500B',
      '$50B to $100B',
      '$20B to $50B',
      '$10B to $20B',
      '$2Bto $10B',
      '$1B to $2B',
      '$500M to $1B',
      '$250M to $500M',
      'Less than $250 million'
    ],
    investing_styles: [
      'Value Investor',
      'Growth Investor',
      'Conservative Investor',
      'GARP',
      'Contrarian',
      'High Growth Investor',
      'Extreme Conservative Investor',
      'Low BETA Stock Focus',
      'High BETA Stock Focus',
      'Long-Term Focus',
      'Short-Term Focus',
      'Small-Cap Focus',
      'Mid-Cap Focus',
      'Micro-Cap Focus',
      'Large-Cap Focus',
      'Mega-Cap Focus',
      'Index Clone',
      'ETF Investor',
      'COMMUNICATIONS concentrated',
      'CONSUMER DISCRETIONARY concentrated',
      'CONSUMER STAPLES concentrated',
      'DIVIDEND PAYER concentrated',
      'ENERGY concentrated',
      'FINANCE concentrated',
      'HEALTH CARE concentrated',
      'INDUSTRIALS concentrated',
      'INFORMATION TECHNOLOGY concentrated',
      'MATERIALS concentrated',
      'MUTUAL FUND concentrated',
      'REAL ESTATE concentrated',
      'TRANSPORTS concentrated',
      'UTILITIES & TELECOMMUNICATIONS concentrated',
      'UTILITIES AND TELECOMMUNICATIONS concentrated'
    ],
    rankings: [
      { value: 'sharpe_three_year_ranking', description: '3-Year Sharpe Ranking' },
      { value: 'calmar_ranking', description: 'Calmar Ranking' },
      { value: 'omega_three_years_ranking', description: 'Omega Ranking' },
      { value: 'turnover_ranking', description: 'Turnover Ranking' },
      { value: 'percent_top_ten_ranking', description: '% Top 10 Ranking' },
      //  {value:"alpha_ranking,",description:"Alpha Ranking"},
      { value: 'sortino_three_year_ranking', description: 'Sortino Ranking' },
      { value: 'efficiency_ranking', description: 'Efficiency Ranking' },
      { value: 'stutzer_three_years_ranking', description: 'Stutzer Ranking' },
      { value: 'stdev_down_three_ranking', description: 'STDDEV Down Ranking' },
      { value: 'information_ratio_ranking', description: 'Information Ratio Ranking' },
      { value: 'losing_months_percentage_ranking', description: 'Losing Months % Ranking' },
      { value: 'treynor_ratio_ranking', description: 'Treynor Ratio Ranking' },
      { value: 'sterling_ranking', description: 'Sterling Ranking' },
      { value: 'tracking_error_ranking', description: 'Tracking Error Ranking' },
      { value: 'upside_ranking', description: 'Upside Ranking' },
      { value: 'kurtosis_ranking', description: 'Kurtosis Ranking' },
      { value: 'skewness_ranking', description: 'Skewness Ranking' },
      { value: 'sdr_sharpe_ranking', description: 'SDR Sharpe Ranking' },
      { value: 'longevity_ranking', description: 'Longevity Ranking' },
      { value: 'stddev_all_funds_ranking', description: 'Differentiation Ranking' }
    ],
    topPerformingTypes: [
      {
        description: 'Do not use dynamic fund selection',
        value: 0,
        requires_subscription: true
      },
      {
        description: 'Top One Year Performance Equal Weight',
        value: 1,
        requires_subscription: true
      },
      {
        description: 'Top Three Year Annualized Equal Weight',
        value: 2,
        requires_subscription: true
      },
      {
        description: 'Top Five Year Annualized Equal Weight',
        value: 3,
        requires_subscription: true
      },
      {
        description: 'Top All-Time Annualized Equal Weight',
        value: 4,
        requires_subscription: true
      },
      {
        description: 'Top One Year Performance Manager Weight',
        value: 5,
        requires_subscription: true
      },
      {
        description: 'Top Three Year Annualized Manager Weight',
        value: 6,
        requires_subscription: true
      },
      {
        description: 'Top Five Year Annualized Manager Weight',
        value: 7,
        requires_subscription: true
      },
      {
        description: 'Top All-Time Annualized Manager Weight',
        value: 8,
        requires_subscription: true
      },
      {
        description: 'Top 3-Year Sharpe Ranking',
        value: 9,
        requires_subscription: true
      },
      {
        description: 'Unique Trades Ranking',
        value: 10,
        requires_subscription: true
      },
      {
        description: 'Quarterly WhaleScore',
        value: 11,
        requires_subscription: true
      },
      {
        description: 'One-Year WhaleScore',
        value: 12,
        requires_subscription: true
      },
      {
        description: 'Three-Year WhaleScore',
        value: 13,
        requires_subscription: true
      },
      {
        description: 'Five-Year WhaleScore',
        value: 14,
        requires_subscription: true
      },
      {
        description: 'Ten-Year WhaleScore',
        value: 15,
        requires_subscription: true
      },
      {
        description: 'All-time Buys Winning %',
        value: 16,
        requires_subscription: false
      },
      {
        description: 'Past 5-Year Buys Winning %',
        value: 17,
        requires_subscription: false
      },
      {
        description: 'Stutzer 3-Year Ranking',
        value: 18,
        requires_subscription: false
      },
      {
        description: 'Small-Cap',
        value: 19,
        requires_subscription: false
      }
    ],
    turnoverChoices: [
      { value: 0, description: 'Any', requires_pro_subscription: false },
      { value: 1, description: 'No turnover', requires_pro_subscription: false },
      { value: 2, description: '< 10%', requires_pro_subscription: false },
      { value: 3, description: '< 25%', requires_pro_subscription: false },
      { value: 4, description: '< 50%', requires_pro_subscription: false },
      { value: 5, description: '<= 100%', requires_pro_subscription: false },
      { value: 6, description: 'Over 100%', requires_pro_subscription: false }
    ],
    top10Choices: [
      { value: 0, description: 'Any', requires_pro_subscription: false },
      { value: 1, description: '> 10%', requires_subscription: true },
      { value: 2, description: '> 20%', requires_pro_subscription: false },
      { value: 3, description: '> 30%', requires_pro_subscription: false },
      { value: 4, description: '> 40%', requires_pro_subscription: false },
      { value: 5, description: '> 50%', requires_pro_subscription: false },
      { value: 6, description: '> 60%', requires_pro_subscription: false },
      { value: 7, description: '> 70%', requires_pro_subscription: false },
      { value: 8, description: '> 80%', requires_pro_subscription: false }
    ]
  }),

  computed: {
    availableRankings() {
      var available = []
      for (var i = 0; i < this.rankings.length; i++) {
        var r = this.rankings[i]
        var found = this.filerPerformance.rankings.find(element => element.type.value == r.value)
        if (!found) {
          available.push(r)
        }
      }
      return available
    },
    backtesterCriteria() {
      return this.whaleTrader
        ? useAppStore().whaleTraderCriteria.signals.f13_signal
        : useAppStore().backtesterCriteria
    },
    filerPerformance() {
      return this.whaleTrader
        ? useAppStore().whaleTraderCriteria.signals.f13_signal.filer_performance
        : useAppStore().backtesterCriteria.filer_performance
    },
    ...mapStoreState('ww', ['sectors']),
    fund_classifications() {
      return useWwStore().allowedClassifications([2, 3, 6, 7, 9, 30, 33, 34])
    },
    /*  ...mapFields({
      backtesterCriteria: 'app.backtesterCriteria',
      filerPerformance: 'app.backtesterCriteria.filer_performance'
    }), */

    isEnabled() {
      return this.filerPerformance && this.filerPerformance.top_performing_type.value > 0
    }
  },

  watch: {
    filerPerformance: {
      deep: true,
      immediate: true,

      handler(newObj, oldObj) {
        this.searching = true

        if (this.isEnabled) {
          this.filerPerformance.selected = true
          this.backtesterCriteria.whalescore = null
          this.backtesterCriteria.heat_map = false
          this.backtesterCriteria.crowded_stocks = false
          this.filerPerformance.use_variance = false
          this.filerPerformance.use_sharpe_ranking = false
          // If there's a selected group currently, reset it
          if (useAppStore().selectedGroup.id) {
            useAppStore().setSelectedGroup(null)
          }
        } else {
          this.filerPerformance.selected = false
        }
        var fp = this.filerPerformance
        if (!fp.rankings) {
          fp.rankings = []
        }
        if (!fp.sc) {
          fp.sc = {
            identifier: 'sector_concentration',
            type: 'dynamic_number_range',
            title: '13F Sector Concentration',
            suffix: '%',
            items: []
          }
        }
        if (!fp.sc.items || fp.sc.items.length == 0) {
          for (var i = 0; i < useWwStore().sectors; i++) {
            var s = cloneDeep(useWwStore().sectors[i])
            s.selected = false
            s.from = null
            s.to = null
            fp.sc.items.push(s)
          }
        }

        this.getMatches()
      }
    }
  },
  mounted() {},

  methods: {
    // Debounce api calls so we don't query the server on every keystroke
    getMatches: debounce(function () {
      api
        .post('filer_perf_count', this.filerPerformance)
        .then(res => {
          this.filerPerformanceMatches = res.data
        })
        .catch(error => console.log(error))
        .then(() => (this.searching = false))
    }, 500),
    resetErrorState(item) {
      item['error'] = null
    },
    updateDynamicNumberRange(filter, item) {
      if ((!item.from && item.from !== 0) || (!item.to && item.to !== 0)) {
        item['error'] = 'Please fill out all the fields!'
        return
      }

      if (item.from > item.to) {
        item['error'] = 'Please enter a valid range!'
        return
      }

      item.value = `${item.id}|${item.from}|${item.to}`
    },

    resetDynamicNumberRange(item) {
      item['error'] = null
      item['from'] = null
      item['to'] = null
    },
    validateRanking(item) {
      var valid = true
      if (
        (!item.min || item.min.trim().length == 0) &&
        (!item.max || item.max.trim().length == 0)
      ) {
        valid = false
      }
      if (!item.type) {
        valid = false
      }
      if (item.min && item.min.trim().length > 0) {
        var val = parseInt(item.min)
        if (val < 0 || val > 100) {
          valid = false
        }
      }
      if (item.max && item.max.trim().length > 0) {
        val = parseInt(item.max)
        if (val < 0 || val > 100) {
          valid = false
        }
      }

      return valid
    },

    editRanking(item) {
      this.editedIndex = this.filerPerformance.rankings.indexOf(item)
      this.editedRanking = Object.assign({}, item)
      this.rankingDialog = true
    },
    deleteRanking(item) {
      const index = this.filerPerformance.rankings.indexOf(item)
      confirm('Are you sure you want to delete this item?') &&
        this.filerPerformance.rankings.splice(index, 1)
    },
    saveRanking() {
      if (this.editedIndex > -1) {
        if (!this.validateRanking(this.editedRanking)) {
          return
        }
        Object.assign(this.filerPerformance.rankings[this.editedIndex], this.editedRanking)
      } else {
        if (!this.validateRanking(this.newRanking)) {
          return
        }
        this.filerPerformance.rankings.push(this.newRanking)
      }
      this.cancelEditRanking()
    },
    cancelEditRanking() {
      this.rankingDialog = false
      setTimeout(() => {
        this.editedRanking = Object.assign({}, { type: null, min: null, max: null })
        this.newRanking = Object.assign({}, { type: null, min: null, max: null })
        this.editedIndex = -1
      }, 300)
    },
    disableMgrOutperform(enabled) {
      if (enabled && this.filerPerformance.mgr_weight_outperformed_equal) {
        this.filerPerformance.mgr_weight_outperformed_equal = !enabled
      }
    },
    disableEqualOutperform(enabled) {
      if (enabled && this.filerPerformance.equal_weight_outperformed_manager) {
        this.filerPerformance.equal_weight_outperformed_manager = !enabled
      }
    }
  }
}
</script>
